/**
 * AP-32 13.1 — **Die Belegliste nach dem Listen-Standard.**
 *
 * `FilterBar`, Spalten-Konfiguration, Sortierung, Paginierung, `HelpPopover`,
 * `LayoutDefaultActions`. Die Reiter Verkauf · Einkauf · Offene Posten ·
 * Entwürfe sind **vorbelegte Filter**, keine eigenen Seiten. Preisspalten tragen
 * `requiresPermission` — Kosmetik; die Sperre ist die Beschneidung im Backend.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Layers, Paperclip, Settings } from 'lucide-react'
import { DELIVERY_BILLING_STATUSES, DOCUMENT_CONFIRMATION_STATES, DOCUMENT_KINDS, DOCUMENT_PAY_STATUSES, DOCUMENT_STATUSES, documentKindsWith, erfuelltRecht } from '@symphenia/shared-types'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import { ExportButton } from '@/components/ui/ExportButton'
import { betreffMitZeitraum } from '@/lib/mail/betreff'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { ohneFehlendeRechte } from '@/components/list/list.types'
import type { ActiveFilter, ColumnFieldDef, FilterFieldDef } from '@/components/list/list.types'
import { pageWide } from '@/lib/form-styles'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { api } from '@/lib/axios'
import { darfPosteingang } from '@/components/eingang/posteingang'
import { BELEG_ZIEL } from '@/components/eingang/texte'
import { documentsApi } from '../api/documents.api'
import { CreateDocumentDialog } from '../components/CreateDocumentDialog'
import { formatBetrag, formatDatum } from '../lib/grid'
import type { AgingBucket, ClerkRef, DocumentListRow, ListDocumentsParams } from '../types/document.types'

const COLUMN_LAYOUT_KEY = 'columnbar_layout_documents'
const DEFAULT_FILTER_FIELD_KEYS = ['q', 'kind', 'partner', 'customerReference', 'status', 'from', 'to']
const DEFAULT_COLUMN_KEYS = ['kind', 'documentDate', 'partnerName', 'customerReference', 'projectNumber', 'subject', 'netTotal', 'grossTotal', 'status', 'confirmation', 'paymentStatus', 'dueDate']
const PREISE = 'correspondence.price.view'
const SERVER_SORTIERBAR = new Set(['documentNumber', 'documentDate', 'partnerName', 'kind', 'status', 'netTotal', 'grossTotal', 'openAmount', 'dueDate', 'createdAt'])

type Reiter = 'all' | 'sales' | 'purchase' | 'open' | 'drafts' | 'toBill'
const REITER: Reiter[] = ['all', 'sales', 'purchase', 'open', 'drafts', 'toBill']

type Uebersetzer = (key: string) => string

const buildColumns = (t: Uebersetzer): ColumnFieldDef[] => [
  { key: 'kind', label: t('documents.fields.kind'), category: 'core' },
  { key: 'documentDate', label: t('documents.fields.documentDate'), category: 'core' },
  { key: 'partnerName', label: t('documents.fields.partner'), category: 'core' },
  { key: 'partnerNumber', label: t('documents.fields.partnerNumber'), category: 'core' },
  { key: 'projectNumber', label: t('documents.fields.project'), category: 'core' },
  { key: 'commission', label: t('documents.fields.commission'), category: 'core' },
  { key: 'customerReference', label: t('documents.fields.orderReference'), category: 'core' },
  { key: 'clerkName', label: t('documents.fields.clerk'), category: 'core' },
  { key: 'billingStatus', label: t('documents.fields.billingStatus'), category: 'core' },
  { key: 'billingReleased', label: t('documents.fields.billingReleased'), category: 'core' },
  // AP-39 11.3: Stand der Auftragsbestätigung — nur Bestellungen.
  { key: 'confirmation', label: t('documents.fields.confirmation'), category: 'core' },
  { key: 'subject', label: t('documents.fields.subject'), category: 'core' },
  { key: 'netTotal', label: t('documents.fields.netTotal'), category: 'price', align: 'right', summable: true, requiresPermission: PREISE },
  { key: 'grossTotal', label: t('documents.fields.grossTotal'), category: 'price', align: 'right', summable: true, requiresPermission: PREISE },
  { key: 'openAmount', label: t('documents.fields.openAmount'), category: 'price', align: 'right', summable: true, requiresPermission: PREISE },
  { key: 'status', label: t('documents.fields.status'), category: 'core' },
  { key: 'paymentStatus', label: t('documents.fields.paymentStatus'), category: 'core' },
  { key: 'dueDate', label: t('documents.fields.dueDate'), category: 'core' },
  { key: 'createdAt', label: t('documents.fields.createdAt'), category: 'core' },
]

const buildFilterFields = (t: Uebersetzer, bearbeiter: ClerkRef[]): FilterFieldDef[] => [
  // Nachtrag 3: Filter nach Sachbearbeiter.
  { key: 'clerkUserId', label: t('documents.fields.clerk'), type: 'select', category: 'core', operators: ['is'], options: bearbeiter.map((b) => ({ value: b.id, label: b.name })) },
  { key: 'q', label: t('documents.fields.search'), type: 'text', category: 'core', operators: ['contains'] },
  // N-59: Empfänger — der Name auf dem Beleg.
  { key: 'partner', label: t('documents.fields.partner'), type: 'text', category: 'core', operators: ['contains'] },
  // N-61: Auftragsnummer — die Bestellnummer des Kunden bzw. „Ihr Zeichen".
  { key: 'customerReference', label: t('documents.fields.orderReference'), type: 'text', category: 'core', operators: ['contains'] },
  // AP-40: Mehrfachauswahl — ein Verweis vom Dashboard filtert etwa „alle Rechnungsarten".
  { key: 'kind', label: t('documents.fields.kind'), type: 'select-multi', category: 'core', operators: ['is'], options: DOCUMENT_KINDS.map((k) => ({ value: k, label: t(`documents.kinds.${k}`) })) },
  { key: 'status', label: t('documents.fields.status'), type: 'select-multi', category: 'core', operators: ['is'], options: DOCUMENT_STATUSES.map((s) => ({ value: s, label: t(`documents.statuses.${s}`) })) },
  { key: 'paymentStatus', label: t('documents.fields.paymentStatus'), type: 'select-multi', category: 'core', operators: ['is'], options: DOCUMENT_PAY_STATUSES.map((s) => ({ value: s, label: t(`documents.payStatuses.${s}`) })) },
  // Nachtrag 6: Lieferscheine nach Verrechnung und Freigabe.
  { key: 'billingStatus', label: t('documents.fields.billingStatus'), type: 'select-multi', category: 'core', operators: ['is'], options: DELIVERY_BILLING_STATUSES.map((s) => ({ value: s, label: t(`documents.billing.statuses.${s}`) })) },
  { key: 'billingReleased', label: t('documents.fields.billingReleased'), type: 'select', category: 'core', operators: ['is'], options: [{ value: 'true', label: t('documents.billing.yes') }, { value: 'false', label: t('documents.billing.no') }] },
  { key: 'from', label: t('documents.fields.dateFrom'), type: 'date', category: 'core', operators: ['is'] },
  { key: 'to', label: t('documents.fields.dateTo'), type: 'date', category: 'core', operators: ['is'] },
  // AP-40 7.3: die Filter hinter den Kacheln „Überfällig" und „Angebote offen".
  { key: 'overdueOnly', label: t('documents.fields.overdueOnly'), type: 'select', category: 'core', operators: ['is'], options: [{ value: 'true', label: t('documents.billing.yes') }] },
  { key: 'openOffers', label: t('documents.fields.openOffers'), type: 'select', category: 'core', operators: ['is'], options: [{ value: 'true', label: t('documents.billing.yes') }] },
  // AP-39 F/G: die Filter hinter den Einkaufs-Kacheln.
  { key: 'unansweredInquiries', label: t('documents.fields.unansweredInquiries'), type: 'select', category: 'core', operators: ['is'], options: [{ value: 'true', label: t('documents.billing.yes') }] },
  {
    key: 'confirmation',
    label: t('documents.fields.confirmation'),
    type: 'select-multi',
    category: 'core',
    operators: ['is'],
    options: [{ value: 'NONE', label: t('documents.confirmation.states.NONE') }, ...DOCUMENT_CONFIRMATION_STATES.map((s) => ({ value: s, label: t(`documents.confirmation.states.${s}`) }))],
  },
]

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

const STANDARD_LAYOUT: ColumnState = { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortKey: 'documentDate', sortDir: 'desc' }

function loadColumnState(): ColumnState {
  const parsed = loadUiSetting<Partial<ColumnState> | null>(COLUMN_LAYOUT_KEY, null)
  if (parsed?.columnKeys?.length) {
    return {
      columnKeys: parsed.columnKeys,
      columnWidths: parsed.columnWidths ?? {},
      sortKey: parsed.sortKey ?? STANDARD_LAYOUT.sortKey,
      sortDir: parsed.sortDir ?? 'desc',
    }
  }
  return STANDARD_LAYOUT
}

/** Filter + Reiter → API-Parameter; ein Reiter ist ein vorbelegter Filter. */
function parameter(filters: Record<string, ActiveFilter>, reiter: Reiter): ListDocumentsParams {
  const p: ListDocumentsParams = {}
  for (const [key, a] of Object.entries(filters)) {
    if (!a.value) continue
    if (key === 'q') p.q = a.value
    if (key === 'kind') p.kind = a.value
    if (key === 'status') p.status = a.value
    if (key === 'paymentStatus') p.paymentStatus = a.value
    if (key === 'clerkUserId') p.clerkUserId = a.value
    if (key === 'partner') p.partner = a.value
    if (key === 'customerReference') p.customerReference = a.value
    if (key === 'billingStatus') p.billingStatus = a.value
    if (key === 'billingReleased') p.billingReleased = a.value === 'true'
    if (key === 'from') p.from = a.value.slice(0, 10)
    if (key === 'to') p.to = a.value.slice(0, 10)
    if (key === 'overdueOnly') p.overdueOnly = a.value === 'true'
    if (key === 'openOffers') p.openOffers = a.value === 'true'
    if (key === 'unansweredInquiries') p.unansweredInquiries = a.value === 'true'
    if (key === 'confirmation') p.confirmation = a.value
  }
  if (reiter === 'sales') p.direction = 'SALES'
  if (reiter === 'purchase') p.direction = 'PURCHASE'
  if (reiter === 'open') p.paymentStatus = 'OPEN,PARTIALLY_PAID,OVERDUE'
  if (reiter === 'drafts') p.status = 'DRAFT'
  // Nachtrag 6: Arbeitsliste der Buchhaltung — freigegebene Lieferscheine, noch nicht vollständig verrechnet.
  if (reiter === 'toBill') {
    p.kind = 'DELIVERY_NOTE'
    p.billingReleased = true
    p.billingStatus = 'NONE,PARTIAL'
  }
  return p
}

const STATUS_FARBE: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  SAVED: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  POSTED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  SENT: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  OPEN: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  PARTIALLY_PAID: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  PAID: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  OVERDUE: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
}

/** AP-39 11.3 — Stand der Auftragsbestätigung an einer Bestellung. */
const BESTAETIGUNG_FARBE: Record<string, string> = {
  CONFIRMED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  CONFIRMED_DEVIATION: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  PARTIAL: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
}

const VERRECHNUNG_FARBE: Record<string, string> = {
  NONE: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  PARTIAL: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  FULL: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
}

export default function DocumentListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const darf = useCallback((p: string) => erfuelltRecht(p, user?.permissions), [user?.permissions])
  const kannAnlegen = documentKindsWith('create', user?.permissions).length > 0

  const [bearbeiter, setBearbeiter] = useState<ClerkRef[]>([])
  useEffect(() => {
    documentsApi
      .clerks()
      .then(({ data }) => setBearbeiter(data.data))
      .catch(() => setBearbeiter([]))
  }, [])

  // AP-39 12.3: Spalte „Eingänge" nur mit Posteingang — die Zahl kommt von dort, nicht aus dem Belegwesen.
  const mitPosteingang = darfPosteingang()
  const allColumns = ohneFehlendeRechte(
    [...buildColumns(t), ...(mitPosteingang ? [{ key: 'inbox', label: 'Eingänge', category: 'core', align: 'right' } satisfies ColumnFieldDef] : [])] as ColumnFieldDef[],
    darf,
  )
  const [eingaenge, setEingaenge] = useState<Record<string, number>>({})
  const filterFields = buildFilterFields(t, bearbeiter)
  // AP-40 6.4: Filter aus der Adresse (Kachel des Dashboards) — einmal beim Öffnen gelesen.
  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() => filterAusAdresse(suchParameter, buildFilterFields(t, [])))

  const [reiter, setReiter] = useState<Reiter>('all')
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(loadColumnState)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [rows, setRows] = useState<DocumentListRow[]>([])
  const [meta, setMeta] = useState({ total: 0, page: 1, totalPages: 1, limit: 50 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [alter, setAlter] = useState<AgingBucket[]>([])

  // 8.4: Alterssummen nur im Reiter „Offene Posten" — und nur mit Preisrecht.
  useEffect(() => {
    if (reiter !== 'open' || !erfuelltRecht(PREISE, user?.permissions)) {
      setAlter([])
      return
    }
    documentsApi
      .aging()
      .then((r) => setAlter(r.data.data))
      .catch(() => setAlter([]))
  }, [reiter, user?.permissions])
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, currentPage: number, state: ColumnState, r: Reiter) => {
      setLoading(true)
      setError(null)
      try {
        const { data } = await documentsApi.list({ ...parameter(filters, r), page: currentPage, limit: 50, sortBy: state.sortKey, sortDir: state.sortDir })
        setRows(data.data)
        setMeta(data.meta)
      } catch (err) {
        setError(fehlertext(err, t))
      } finally {
        setLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    void fetchData(filterValues, page, colState, reiter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, reiter])

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(neu, 1, colState, reiter), 400)
    },
    [fetchData, colState, reiter],
  )

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(COLUMN_LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleSort = (key: string) => {
    if (!SERVER_SORTIERBAR.has(key)) return
    const dir: 'asc' | 'desc' = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: dir }
    updateColState(() => next)
    setPage(1)
    void fetchData(filterValues, 1, next, reiter)
  }

  // Die Zahl der Eingänge nur, wenn die Spalte gezeigt wird — eine Anfrage je Seite.
  const zeigtEingaenge = mitPosteingang && colState.columnKeys.includes('inbox')
  useEffect(() => {
    const ids = zeigtEingaenge ? rows.filter((z) => BELEG_ZIEL[z.kind]).map((z) => z.id) : []
    if (!ids.length) return setEingaenge({})
    let aus = false
    api
      .get<{ data: Record<string, number> }>('/inbox/assignments/count', { params: { targetIds: ids.join(',') } })
      .then((r) => !aus && setEingaenge(r.data.data))
      .catch(() => !aus && setEingaenge({}))
    return () => {
      aus = true
    }
  }, [rows, zeigtEingaenge])

  const shownColumns = colState.columnKeys.map((k) => allColumns.find((c) => c.key === k)).filter((c): c is ColumnFieldDef => !!c)
  const availableToAdd = allColumns.filter((c) => !colState.columnKeys.includes(c.key))

  const sortSymbol = (key: string) =>
    colState.sortKey === key ? (colState.sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />

  /** Nachtrag 6: Freigabe direkt in der Liste — der Sachbearbeiter hakt ab, die Buchhaltung sieht es im Reiter „Zur Verrechnung". */
  const freigabeSchalten = async (row: DocumentListRow) => {
    try {
      await documentsApi.setBilling(row.id, { released: !row.billingReleased })
      void fetchData(filterValues, page, colState, reiter)
    } catch (err) {
      setError(fehlertext(err, t))
    }
  }

  const plakette = (text: string, farbe: string) => <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${farbe}`}>{text}</span>

  const renderCell = (row: DocumentListRow, col: ColumnFieldDef): React.ReactNode => {
    switch (col.key) {
      case 'inbox':
        return eingaenge[row.id] ? <span title="Dokumente aus dem Posteingang">{eingaenge[row.id]}</span> : ''
      case 'kind':
        return t(`documents.kinds.${row.kind}`)
      case 'documentDate':
        return formatDatum(row.documentDate)
      case 'dueDate':
        return formatDatum(row.dueDate)
      case 'createdAt':
        return new Date(row.createdAt).toLocaleDateString(navigator.language)
      case 'netTotal':
      case 'grossTotal':
      case 'openAmount':
        return formatBetrag(row[col.key], row.currency)
      case 'status':
        // N-13: Ein nummerierter, offener Beleg (Art ohne Festschreiben) ist kein Entwurf.
        // Nachtrag 6: „Entwurf" nur, wo festgeschrieben werden muss — sonst ist der Beleg „Gespeichert".
        return row.status === 'DRAFT' && !row.requirePosting
          ? plakette(t('documents.statuses.SAVED'), STATUS_FARBE.SAVED)
          : plakette(t(`documents.statuses.${row.status}`), STATUS_FARBE[row.status] ?? '')
      case 'billingStatus':
        return row.billingStatus
          ? plakette(`${t(`documents.billing.statuses.${row.billingStatus}`)}${row.billingManual ? ' ✎' : ''}`, VERRECHNUNG_FARBE[row.billingStatus])
          : '—'
      case 'confirmation':
        return row.confirmation ? (
          <span title={[row.confirmation.externalNumber, row.confirmation.deviationText].filter(Boolean).join(' · ') || undefined}>
            {plakette(
              `${t(`documents.confirmation.states.${row.confirmation.state}`)}${row.confirmation.deviationCount ? ` (${row.confirmation.deviationCount})` : ''}`,
              BESTAETIGUNG_FARBE[row.confirmation.state] ?? '',
            )}
          </span>
        ) : (
          '—'
        )
      case 'billingReleased':
        if (row.kind !== 'DELIVERY_NOTE') return '—'
        return (
          <span className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={row.billingReleased}
              disabled={row.status === 'CANCELLED' || !darf('correspondence.delivery_note.update')}
              onChange={() => void freigabeSchalten(row)}
              title={t('documents.billing.released')}
              aria-label={t('documents.billing.released')}
            />
            {row.hasSignedPdf && <Paperclip className="h-3.5 w-3.5 text-muted-foreground" aria-label={t('documents.billing.signedPdf')} />}
          </span>
        )
      case 'paymentStatus':
        return row.paymentStatus === 'NONE' ? '—' : plakette(t(`documents.payStatuses.${row.paymentStatus}`), STATUS_FARBE[row.paymentStatus] ?? '')
      default: {
        const wert = row[col.key as keyof DocumentListRow]
        return wert === null || wert === undefined || wert === '' ? '—' : String(wert)
      }
    }
  }

  const summe = (key: string) => {
    const waehrungen = new Set(rows.map((r) => r.currency))
    if (waehrungen.size !== 1) return ''
    const wert = rows.reduce((s, r) => s + ((r[key as keyof DocumentListRow] as number | undefined) ?? 0), 0)
    return formatBetrag(wert, rows[0].currency)
  }

  /** Export mit denselben Filtern, demselben Reiter und denselben Spalten wie die Liste. */
  const handleExport = (format: 'xlsx' | 'docx' | 'pdf', orientation?: 'portrait' | 'landscape') => {
    const filterInfo = Object.entries(filterValues)
      .filter(([, a]) => a.value)
      .map(([key, a]) => {
        const feld = filterFields.find((f) => f.key === key)
        const wert = feld?.options?.find((o) => o.value === a.value)?.label ?? a.value
        return `${feld?.label ?? key}: ${wert}`
      })
    return documentsApi.exportRequest({
      ...parameter(filterValues, reiter),
      sortBy: colState.sortKey,
      sortDir: colState.sortDir,
      format,
      orientation,
      filterInfo: [reiter !== 'all' ? t(`documents.tabs.${reiter}`) : '', ...filterInfo].filter(Boolean).join(' · ') || undefined,
      // „Eingänge" kennt der Export des Belegwesens nicht — die Zahl gehört dem Posteingang.
      columns: [{ key: 'documentNumber', label: t('documents.fields.number') }, ...shownColumns.filter((c) => c.key !== 'inbox').map((c) => ({ key: c.key, label: c.label }))],
    })
  }

  const markierteLieferscheine = rows.filter((r) => markiert.has(r.id))
  const sammelrechnungMoeglich =
    markierteLieferscheine.length >= 2 && markierteLieferscheine.every((r) => r.kind === 'DELIVERY_NOTE') && darf('correspondence.invoice.create')

  const sammelrechnung = async () => {
    try {
      const { data } = await documentsApi.collectInvoice(markierteLieferscheine.map((r) => r.id))
      navigate(`/correspondence/${data.data.id}`)
    } catch (err) {
      setError(fehlertext(err, t))
    }
  }

  const totalCols = 2 + shownColumns.length + (columnConfigMode && availableToAdd.length ? 1 : 0) + 1

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('documents.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('documents.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sammelrechnungMoeglich && (
            <button type="button" onClick={() => void sammelrechnung()} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
              <Layers className="h-4 w-4" />
              {t('documents.actions.collectInvoice', { count: markierteLieferscheine.length })}
            </button>
          )}
          <ExportButton
            request={handleExport}
            defaultFilename="belege"
            mail={{
              source: 'EXPORT_DOCUMENTS',
              title: t('mailExport.titles.EXPORT_DOCUMENTS'),
              subject: betreffMitZeitraum(t('mailExport.titles.EXPORT_DOCUMENTS'), filterValues['from']?.value, filterValues['to']?.value),
            }}
          />
          {/* N-62: Mahnwesen und Einstellungen stehen im Menü „Schriftverkehr" — hier keine zweiten Wege. */}
          {kannAnlegen && (
            <button type="button" onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
              + {t('documents.create')}
            </button>
          )}
        </div>
      </div>

      {reiter === 'open' && alter.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {alter.flatMap((a) =>
            (['notDue', 'd1_30', 'd31_60', 'over60', 'total'] as const).map((k) => (
              <div key={`${a.currency}-${k}`} className="rounded-lg border border-border bg-card px-3 py-2 shadow-sm">
                <p className="text-xs text-muted-foreground">
                  {t(`documents.openItems.${k}`)}
                  {alter.length > 1 ? ` · ${a.currency}` : ''}
                </p>
                <p className={`text-lg font-semibold tabular-nums ${k === 'over60' && a[k] > 0 ? 'text-destructive' : ''}`}>{formatBetrag(a[k], a.currency)}</p>
              </div>
            )),
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1 border-b border-border">
        {REITER.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => {
              setReiter(r)
              setPage(1)
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${reiter === r ? 'border-primary font-medium text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {t(`documents.tabs.${r}`)}
          </button>
        ))}
      </div>

      <FilterBar availableFields={filterFields} value={filterValues} onChange={handleFiltersChange} layoutKey="documents" defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS} adresse={adresse} />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          title={t('common.configureColumns', { defaultValue: 'Spalten konfigurieren' })}
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? STANDARD_LAYOUT
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColState(next)
                void fetchData(filterValues, 1, next, reiter)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="w-8 px-2 py-1.5" />
                <th className="px-4 py-1.5 text-left font-medium text-foreground">
                  <button type="button" onClick={() => handleSort('documentNumber')} className="flex items-center gap-1 hover:text-primary">
                    {t('documents.fields.number')}
                    {sortSymbol('documentNumber')}
                  </button>
                </th>
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-4 py-1.5 font-medium text-foreground ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      {SERVER_SORTIERBAR.has(col.key) ? (
                        <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                          {col.label}
                          {sortSymbol(col.key)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button type="button" onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))} className="ml-1 text-muted-foreground hover:text-destructive">
                          ✕
                        </button>
                      )}
                    </div>
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ {t('common.column', { defaultValue: 'Spalte' })}</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('documents.empty')}
                  </td>
                </tr>
              )}
              {rows.map((row, idx) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(`/correspondence/${row.id}`)}
                  className={`cursor-pointer border-b border-border transition-colors last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} hover:bg-accent/30`}
                >
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={markiert.has(row.id)}
                      onChange={() =>
                        setMarkiert((v) => {
                          const neu = new Set(v)
                          if (neu.has(row.id)) neu.delete(row.id)
                          else neu.add(row.id)
                          return neu
                        })
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-4 py-1.5 font-mono text-muted-foreground">
                    {row.documentNumber ?? <span className="italic">{row.draftLabel}</span>}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`whitespace-nowrap px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {renderCell(row, col)}
                    </td>
                  ))}
                  {columnConfigMode && availableToAdd.length > 0 && <td />}
                  <td />
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td />
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                    {loading ? t('common.loading') : rows.length < meta.total ? `${rows.length} / ${meta.total}` : `${meta.total}`} {t('documents.count')}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {col.summable ? summe(col.key) : ''}
                    </td>
                  ))}
                  {columnConfigMode && availableToAdd.length > 0 && <td />}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>
              {meta.page} / {meta.totalPages} ({meta.total})
            </span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ‹
              </button>
              <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      <CreateDocumentDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => navigate(`/correspondence/${id}`)} />
    </div>
  )
}
